import React from 'react';
import { Table, Button, Input, InputNumber, DatePicker, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FJ133, FG031 } from '../../../services/j203Api';

const { Title } = Typography;

interface Tab3Props {
  rows: Partial<FJ133>[];
  paymentRows: Partial<FG031>[];
  isEditing: boolean;
  onRowsChange: (updater: (prev: Partial<FJ133>[]) => Partial<FJ133>[]) => void;
  onPaymentRowsChange: (updater: (prev: Partial<FG031>[]) => Partial<FG031>[]) => void;
}

const Tab3Extras: React.FC<Tab3Props> = ({ rows, paymentRows, isEditing, onRowsChange, onPaymentRowsChange }) => {
  // --- FJ133 附加明細 ---
  const addRow = () => {
    onRowsChange(prev => [...prev, {
      fj133_02: String(prev.length + 1),
      fj133_11: null,
      fj133_12: 0,
    }]);
  };

  const removeRow = (idx: number) => {
    onRowsChange(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fj133_02: String(i + 1) })));
  };

  const updateField = (idx: number, key: keyof FJ133, value: unknown) => {
    onRowsChange(prev => prev.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  };

  const columns = [
    { title: <span data-contract-field="fj133_sequence">序號</span>, width: 60, render: (_: unknown, __: unknown, i: number) => i + 1 },
    {
      title: <span data-contract-field="fj133_02">發票號碼</span>, dataIndex: 'fj133_02', width: 120,
      render: (v: string, _: unknown, i: number) => isEditing
        ? <Input size="small" value={v || ''} onChange={e => updateField(i, 'fj133_02', e.target.value)} />
        : (v || ''),
    },
    {
      title: <span data-contract-field="fj133_11">發票日期</span>, dataIndex: 'fj133_11',
      render: (v: string | null, _: unknown, i: number) => isEditing
        ? <DatePicker size="small"
            value={v ? dayjs(v) : null}
            onChange={d => updateField(i, 'fj133_11', d ? d.toISOString() : null)}
            style={{ width: '100%' }}
          />
        : (v ? dayjs(v).format('YYYY-MM-DD') : ''),
    },
    {
      title: <span data-contract-field="fj133_12">發票金額</span>, dataIndex: 'fj133_12', width: 140,
      render: (v: number, _: unknown, i: number) => isEditing
        ? <InputNumber size="small" value={v} onChange={val => updateField(i, 'fj133_12', val || 0)} style={{ width: '100%' }} />
        : (v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }),
    },
    ...(isEditing ? [{
      title: '', width: 40,
      render: (_: unknown, __: unknown, i: number) => (
        <Button size="small" danger type="link" icon={<DeleteOutlined />} onClick={() => removeRow(i)} />
      ),
    }] : []),
  ];

  // --- FG031 繳款明細 ---
  const addPaymentRow = () => {
    onPaymentRowsChange(prev => [...prev, {
      fg031_02: String(prev.length + 1),
      fg031_03: '',
      fg031_11: 0,
      fg031_12: null,
    }]);
  };

  const removePaymentRow = (idx: number) => {
    onPaymentRowsChange(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fg031_02: String(i + 1) })));
  };

  const updatePaymentField = (idx: number, key: keyof FG031, value: unknown) => {
    onPaymentRowsChange(prev => prev.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  };

  const paymentColumns = [
    { title: <span data-contract-field="fg031_sequence">序號</span>, width: 60, render: (_: unknown, __: unknown, i: number) => i + 1 },
    {
      title: <span data-contract-field="fg031_03">繳款單號</span>, dataIndex: 'fg031_03', width: 180,
      render: (v: string, _: unknown, i: number) => isEditing
        ? <Input size="small" value={v || ''} onChange={e => updatePaymentField(i, 'fg031_03', e.target.value)} />
        : (v || ''),
    },
    {
      title: <span data-contract-field="fg031_12">繳款日期</span>, dataIndex: 'fg031_12', width: 160,
      render: (v: string | null, _: unknown, i: number) => isEditing
        ? <DatePicker size="small"
            value={v ? dayjs(v) : null}
            onChange={d => updatePaymentField(i, 'fg031_12', d ? d.toISOString() : null)}
            style={{ width: '100%' }}
          />
        : (v ? dayjs(v).format('YYYY-MM-DD') : ''),
    },
    {
      title: <span data-contract-field="fg031_11">繳款金額</span>, dataIndex: 'fg031_11', width: 140,
      render: (v: number, _: unknown, i: number) => isEditing
        ? <InputNumber size="small" value={v} onChange={val => updatePaymentField(i, 'fg031_11', val || 0)} style={{ width: '100%' }} />
        : (v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }),
    },
    ...(isEditing ? [{
      title: '', width: 40,
      render: (_: unknown, __: unknown, i: number) => (
        <Button size="small" danger type="link" icon={<DeleteOutlined />} onClick={() => removePaymentRow(i)} />
      ),
    }] : []),
  ];

  return (
    <div>
      {/* FJ133 發票明細 */}
      <Title level={5} style={{ marginBottom: 8 }}>發票明細</Title>
      {isEditing && (
        <div style={{ marginBottom: 8 }}>
          <Button size="small" icon={<PlusOutlined />} onClick={addRow}>新增附加明細</Button>
        </div>
      )}
      <Table dataSource={rows} columns={columns} rowKey={(_, i) => String(i)} size="small" pagination={false} />

      {/* FG031 繳款明細 */}
      <Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>繳款明細</Title>
      {isEditing && (
        <div style={{ marginBottom: 8 }}>
          <Button size="small" icon={<PlusOutlined />} onClick={addPaymentRow}>新增繳款明細</Button>
        </div>
      )}
      <Table dataSource={paymentRows} columns={paymentColumns} rowKey={(_, i) => String(i)} size="small" pagination={false} />
    </div>
  );
};

export default React.memo(Tab3Extras);
